/*
 * <oilogic-announcement-bar> — scroll-snap slider for the announcement strip.
 *
 * Progressive enhancement: the track is a native scroll-snap overflow scroller,
 * so touch/trackpad swiping works with zero JS. This element only adds mouse
 * drag, the optional prev/next arrows, and optional autoplay
 * (data-autoplay="seconds"). Autoplay is skipped under prefers-reduced-motion
 * and pauses on hover/focus. Re-inits on Theme Editor reload.
 */
class OilogicAnnouncementBar extends HTMLElement {
  #ctrl;
  #timer = 0;

  connectedCallback() {
    this.#ctrl = new AbortController();
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    const { signal } = this.#ctrl;
    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.#step(1), { signal });

    if (this.track.children.length > 1) {
      this.#initDrag(signal);
      this.#initAutoplay(signal);
    }
  }

  disconnectedCallback() {
    this.#ctrl.abort();
    this.#stop();
  }

  #index() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  #goTo(i) {
    const count = this.track.children.length;
    const target = ((i % count) + count) % count;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  }

  #step(dir) {
    this.#goTo(this.#index() + dir);
  }

  #initDrag(signal) {
    const track = this.track;
    let startX = 0;
    let startLeft = 0;
    let dragged = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      dragged = false;
      track.setPointerCapture(e.pointerId);
    }, { signal });

    track.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !track.hasPointerCapture(e.pointerId)) return;
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) < 5) return;
      dragged = true;
      track.classList.add('announcement-bar__track--dragging');
      track.scrollLeft = startLeft - dx;
    }, { signal });

    const end = (e) => {
      if (e.pointerType !== 'mouse' || !dragged) return;
      track.classList.remove('announcement-bar__track--dragging');
      const from = startLeft / track.clientWidth;
      const now = track.scrollLeft / track.clientWidth;
      let target = Math.round(now);
      if (target === Math.round(from) && Math.abs(now - from) > 0.08) {
        target = now > from ? Math.ceil(now) : Math.floor(now);
      }
      this.#goTo(target);
    };
    track.addEventListener('pointerup', end, { signal });
    track.addEventListener('pointercancel', end, { signal });

    // A real drag over a linked slide must not fire the link on release.
    track.addEventListener('click', (e) => {
      if (!dragged) return;
      dragged = false;
      e.preventDefault();
      e.stopPropagation();
    }, { capture: true, signal });
  }

  #initAutoplay(signal) {
    const seconds = parseFloat(this.dataset.autoplay);
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const start = () => {
      this.#stop();
      this.#timer = setInterval(() => this.#step(1), seconds * 1000);
    };

    this.addEventListener('pointerenter', () => this.#stop(), { signal });
    this.addEventListener('pointerleave', start, { signal });
    this.addEventListener('focusin', () => this.#stop(), { signal });
    this.addEventListener('focusout', start, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#stop();
      else start();
    }, { signal });

    start();
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = 0;
  }
}

if (!customElements.get('oilogic-announcement-bar')) {
  customElements.define('oilogic-announcement-bar', OilogicAnnouncementBar);
}
